import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { devicesAPI, adminAPI } from '../services/api';
import { Unlock, Eye, RefreshCw, X } from 'lucide-react';

export default function Lockers() {
  const [searchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(searchParams.get('device') || '');
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [, setBoxHistory] = useState([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    devicesAPI.list().then(res => {
      setDevices(res.data);
      if (!selectedDevice && res.data.length > 0) setSelectedDevice(res.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (selectedDevice) loadBoxes();
  }, [selectedDevice]);

  const loadBoxes = async () => {
    setLoading(true);
    setLoadError('');
    try {
      // boxes-detail syncs with the locker itself, so no separate sync call
      const res = await devicesAPI.getBoxesDetail(selectedDevice);
      setBoxes(res.data);
    } catch (e) {
      // Don't fall back to a list without parcel data: it would make boxes
      // with parcels look empty/stuck.
      setLoadError(e.response?.data?.detail || 'Could not read this locker. Try Refresh.');
    }
    setLoading(false);
  };

  const openBox = async (box) => {
    const what = box.order_no
      ? `It holds order ${box.order_no}${box.recipient ? ` for ${box.recipient}` : ''}${box.parcel_status === 'stored' ? ' (a stored parcel — the customer has not picked it up)' : ''}.`
      : 'It has no parcel.';
    if (!window.confirm(`Open box ${box.box_name} now? ${what}\n\nThe door will open at the locker.`)) return;
    setActionLoading(true);
    try {
      const res = await devicesAPI.openBox(selectedDevice, box.id);
      alert(res.data.message || 'Box open command sent');
      setModal(null);
      loadBoxes();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setActionLoading(false);
  };

  const getBoxColor = (box) => {
    if (!box.is_enabled) return { bg: '#1e2029', border: '#252733', color: '#8a8da4', label: 'Disabled' };
    if (box.is_door_open) return { bg: 'rgba(255,68,102,0.1)', border: 'rgba(255,68,102,0.3)', color: '#ff4466', label: 'Open' };
    if (box.is_occupied) return { bg: 'rgba(255,176,32,0.1)', border: 'rgba(255,176,32,0.3)', color: '#ffb020', label: 'Occupied' };
    return { bg: 'rgba(139,198,62,0.1)', border: 'rgba(139,198,62,0.3)', color: '#8BC63E', label: 'Available' };
  };

  const device = devices.find(d => d.id === selectedDevice);

  // ── Release all ──
  // Stuck = occupied with no parcel, or a booking nobody used for 2 hours.
  // Boxes with a stored parcel only when asked. Never touched: a delivery in
  // progress (courier opened the box) or a booking younger than 2 hours (the
  // courier may be on the way).
  const BOOKING_KEEP_MIN = 120;
  const [bulk, setBulk] = useState(null); // {includeStored, running, done, results:[], current}
  const isStored = (b) => b.parcel_status === 'stored' || b.parcel_status === 'pending';
  // An order Bestwond holds that Damitove never created: probably stale, but
  // could be a parcel stored at the locker's own screen, so it's opt-in.
  const isBwOnly = (b) => !b.parcel_status && !!b.bestwond_order_no;
  const isLiveBooking = (b) => b.parcel_status === 'draft' &&
    (b.parcel_opened || b.parcel_age_min == null || b.parcel_age_min < BOOKING_KEEP_MIN);
  const groups = (list) => {
    const occ = list.filter(b => b.is_enabled && b.is_occupied);
    return {
      stuck: occ.filter(b => !isStored(b) && !isLiveBooking(b) && !isBwOnly(b)),
      stored: occ.filter(isStored),
      bwOnly: occ.filter(isBwOnly),
      live: occ.filter(isLiveBooking),
    };
  };
  const { stuck: stuckBoxes, stored: storedBoxes, live: freshBoxes, bwOnly: bwOnlyBoxes } = groups(boxes);
  const names = (list) => list.map(b => b.box_name).join(', ');

  const runReleaseAll = async () => {
    let targets = [...stuckBoxes, ...(bulk.includeBwOnly ? bwOnlyBoxes : []), ...(bulk.includeStored ? storedBoxes : [])];
    if (bulk.includeStored && storedBoxes.length > 0 &&
        !window.confirm(`Release ${storedBoxes.length} box(es) WITH stored parcels (${names(storedBoxes)})? ` +
                        'Those parcels are cancelled and their pickup codes stop working.')) return;
    setBulk(b => ({ ...b, running: true, results: [], current: 'checking the locker again' }));
    // Re-read the locker: only release boxes that were listed AND still qualify.
    let fresh;
    try { fresh = groups((await devicesAPI.getBoxesDetail(selectedDevice)).data); }
    catch (e) { setBulk(b => ({ ...b, running: false, done: true, current: null, results: [{ box: '—', st: 'failed', msg: 'Could not re-read the locker; nothing was released.' }] })); return; }
    const still = new Set([...fresh.stuck, ...(bulk.includeBwOnly ? fresh.bwOnly : []),
                           ...(bulk.includeStored ? fresh.stored : [])].map(b => b.id));
    const results = [];
    for (const box of targets.filter(b => !still.has(b.id))) {
      results.push({ box: box.box_name, st: 'skipped', msg: 'changed since the list was shown — left alone' });
    }
    targets = targets.filter(b => still.has(b.id));
    for (let i = 0; i < targets.length; i++) {
      const box = targets[i];
      setBulk(b => ({ ...b, current: `${i + 1}/${targets.length} — box ${box.box_name}` }));
      let row;
      try {
        const r = await devicesAPI.forceRelease(selectedDevice, box.id);
        // Kerong opens the door to release: the box is free once it's shut.
        const st = r.data.cleared !== false ? 'cleared' : r.data.opened === true ? 'opened' : 'failed';
        row = { box: box.box_name, st, msg: r.data.message };
      } catch (e) {
        row = { box: box.box_name, st: 'failed', msg: e.response?.data?.detail || e.message || 'Error' };
      }
      results.push(row);
      setBulk(b => ({ ...b, results: [...results] }));
    }
    setBulk(b => ({ ...b, running: false, done: true, current: null }));
    loadBoxes();
  };

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Locker Boxes</h1>
        <button style={s.outlineBtn} onClick={loadBoxes}><RefreshCw size={14} /> Refresh</button>
        <button style={{...s.outlineBtn, marginLeft: 8, borderColor: 'rgba(255,68,102,0.3)', color: '#ff4466'}}
          disabled={!selectedDevice || loading}
          onClick={() => setBulk({ includeStored: false, includeBwOnly: false, running: false, done: false, results: [], current: null })}>
          Release All…
        </button>
        <button style={{...s.outlineBtn, marginLeft: 8, borderColor: 'rgba(139,198,62,0.3)', color: '#8BC63E'}} onClick={async () => { if(selectedDevice){ try { const r = await devicesAPI.syncDoorStatus(selectedDevice); alert(r.data.message); loadBoxes(); } catch(e){ alert('Error syncing'); }}}}>Check Door Status</button>
      </div>

      {loadError && <div style={{ background: 'rgba(255,68,102,0.1)', border: '1px solid rgba(255,68,102,0.3)', borderRadius: 8, padding: '10px 14px', color: '#ff4466', fontSize: 13, marginBottom: 14 }}>{loadError}</div>}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <label style={s.label}>Select Device</label>
          <select style={s.select} value={selectedDevice} onChange={(e) => setSelectedDevice(e.target.value)}>
            <option value="">Choose...</option>
            {devices.map(d => (
              <option key={d.id} value={d.id}>{d.device_number} — {d.name}</option>
            ))}
          </select>
        </div>
        {device && (
          <div style={{ alignSelf: 'flex-end', fontSize: 13, color: '#8789a0' }}>
            {boxes.length} boxes · {boxes.filter(b => !b.is_occupied && b.is_enabled).length} available
          </div>
        )}
      </div>

      {/* Visual Grid */}
      <div style={s.panel}>
        <div style={s.panelHead}>
          <span style={s.panelTitle}>Locker Map</span>
          <div style={s.legend}>
            <span style={s.legendItem}><span style={{ ...s.legendDot, background: '#8BC63E' }} /> Available</span>
            <span style={s.legendItem}><span style={{ ...s.legendDot, background: '#ffb020' }} /> Occupied</span>
            <span style={s.legendItem}><span style={{ ...s.legendDot, background: '#ff4466' }} /> Door Open</span>
            <span style={s.legendItem}><span style={{ ...s.legendDot, background: '#565870' }} /> Disabled</span>
          </div>
        </div>
        <div style={s.gridWrap}>
          {loading ? (
            <div style={s.empty}>Loading boxes...</div>
          ) : boxes.length === 0 ? (
            <div style={s.empty}>Select a device to view boxes</div>
          ) : (
            <div style={s.grid}>
              {boxes.map((box) => {
                const c = getBoxColor(box);
                return (
                  <div
                    key={box.id}
                    style={{ ...s.cell, background: c.bg, borderColor: c.border, color: c.color }}
                    onClick={async () => {
                    if (!box.is_enabled) return;
                    setModal(box);
                    try {
                      const dev = devices.find(d => d.id === selectedDevice);
                      if (dev) {
                        const h = await adminAPI.boxHistory(dev.device_number, box.box_name);
                        setBoxHistory(h.data);
                      }
                    } catch(e) { setBoxHistory([]); }
                  }}
                  >
                    <div style={s.cellName}>{box.box_name}</div>
                    <div style={s.cellSize}>{box.box_size}</div>
                    <div style={s.cellStatus}>{c.label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Box Table */}
      <div style={{ ...s.panel, marginTop: 20 }}>
        <div style={s.panelHead}><span style={s.panelTitle}>Box Details</span></div>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Box</th>
              <th style={s.th}>Lock Address</th>
              <th style={s.th}>Size</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Order</th>
              <th style={s.th}>Sender</th>
              <th style={s.th}>Enabled</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {boxes.map((box) => {
              const c = getBoxColor(box);
              return (
                <tr key={box.id}>
                  <td style={s.td}><strong>{box.box_name}</strong></td>
                  <td style={s.td}><span style={s.mono}>{box.lock_address}</span></td>
                  <td style={s.td}>{box.box_size === 'S' ? 'Small' : box.box_size === 'M' ? 'Medium' : 'Large'}</td>
                  <td style={s.td}><span style={{ ...s.badge, background: c.bg, color: c.color }}>{c.label}</span></td>
                  <td style={s.td}>{box.order_no ? <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11 }}>{box.order_no}</span> : '—'}</td>
                  <td style={s.td}>{box.sender || '—'}</td>
                  <td style={s.td}>{box.is_enabled ? '✓' : '✗'}</td>
                  <td style={s.td}>
                    <button style={s.smBtn} onClick={() => setModal(box)}><Unlock size={12} /> Open</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Release All Modal */}
      {bulk && (
        <div style={s.overlay} onClick={(e) => { if (e.target === e.currentTarget && !bulk.running) setBulk(null); }}>
          <div style={{ ...s.modal, width: 460 }}>
            <div style={s.modalHead}>
              <h3>Release all — {device ? device.device_number : ''}</h3>
              {!bulk.running && <X size={18} style={{ cursor: 'pointer', color: '#8789a0' }} onClick={() => setBulk(null)} />}
            </div>
            <div style={{ ...s.modalBody, fontSize: 13, color: '#c0c2d0', lineHeight: 1.6 }}>
              {!bulk.running && !bulk.done && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <strong style={{ color: '#e4e5ed' }}>{stuckBoxes.length} stuck box(es)</strong> — no parcel, or a booking unused for 2+ hours
                    {stuckBoxes.length > 0 && <>: <span style={s.mono}>{names(stuckBoxes)}</span></>}
                  </div>
                  {storedBoxes.length > 0 && (
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={bulk.includeStored}
                        onChange={e => setBulk(b => ({ ...b, includeStored: e.target.checked }))} style={{ marginTop: 4 }} />
                      <span>Also release <strong style={{ color: '#ff4466' }}>{storedBoxes.length} box(es) with stored parcels</strong>
                        {' '}(<span style={s.mono}>{names(storedBoxes)}</span>). Those parcels are cancelled and their pickup codes stop working.</span>
                    </label>
                  )}
                  {bwOnlyBoxes.length > 0 && (
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!bulk.includeBwOnly}
                        onChange={e => setBulk(b => ({ ...b, includeBwOnly: e.target.checked }))} style={{ marginTop: 4 }} />
                      <span>Also release <strong style={{ color: '#ffb020' }}>{bwOnlyBoxes.length} box(es) with an order only in Bestwond</strong>
                        {' '}(<span style={s.mono}>{names(bwOnlyBoxes)}</span>) — not created through Damitove; usually stale, but check
                        nobody stored a parcel at the locker screen.</span>
                    </label>
                  )}
                  {freshBoxes.length > 0 && (
                    <div style={{ marginBottom: 12, color: '#8789a0' }}>
                      Not touched: {freshBoxes.length} delivery/booking(s) in progress (<span style={s.mono}>{names(freshBoxes)}</span>) — opened by a courier or booked less than 2 hours ago.
                    </div>
                  )}
                  <div style={{ marginBottom: 16, color: '#8789a0' }}>
                    Kerong: each released box's door opens. Bestwond: the door opens only if clearing the order fails.
                  </div>
                  <button style={{ ...s.primaryBtn, width: '100%', background: '#ff4466', color: '#fff' }}
                    disabled={(stuckBoxes.length + (bulk.includeBwOnly ? bwOnlyBoxes.length : 0) + (bulk.includeStored ? storedBoxes.length : 0)) === 0}
                    onClick={runReleaseAll}>
                    Release {(stuckBoxes.length + (bulk.includeBwOnly ? bwOnlyBoxes.length : 0) + (bulk.includeStored ? storedBoxes.length : 0))} box(es)
                  </button>
                </>
              )}
              {bulk.running && <div style={{ marginBottom: 12, color: '#ffb020' }}>Releasing {bulk.current}… keep this page open.</div>}
              {bulk.done && <div style={{ marginBottom: 12, color: '#e4e5ed' }}>
                Done: {bulk.results.filter(r => r.st === 'cleared').length} cleared
                {bulk.results.some(r => r.st === 'opened') && <>, {bulk.results.filter(r => r.st === 'opened').length} released and opened — close their doors</>}
                , {bulk.results.filter(r => r.st === 'failed').length} not cleared
                {bulk.results.some(r => r.st === 'skipped') && <>, {bulk.results.filter(r => r.st === 'skipped').length} left alone</>}.
              </div>}
              {bulk.results.length > 0 && (
                <div style={{ maxHeight: 260, overflowY: 'auto', borderTop: '1px solid #252733', paddingTop: 8 }}>
                  {bulk.results.map(r => (
                    <div key={r.box} style={{ padding: '4px 0', color: r.st === 'cleared' ? '#8BC63E' : r.st === 'opened' ? '#ffb020' : r.st === 'skipped' ? '#8789a0' : '#ff4466' }}>
                      {r.st === 'failed' ? '✗' : r.st === 'skipped' ? '–' : '✓'} <span style={s.mono}>{r.box}</span> <span style={{ color: '#8789a0' }}>{r.msg}</span>
                    </div>
                  ))}
                </div>
              )}
              {bulk.done && (
                <button style={{ ...s.outlineBtn, width: '100%', marginTop: 12 }} onClick={() => setBulk(null)}>Close</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Box Action Modal */}
      {modal && (
        <div style={s.overlay} onClick={(e) => { if(e.target === e.currentTarget) { setModal(null); setBoxHistory([]); }}}>
          <div style={s.modal}>
            <div style={s.modalHead}>
              <h3>Box {modal.box_name}</h3>
              <X size={18} style={{ cursor: 'pointer', color: '#8789a0' }} onClick={() => setModal(null)} />
            </div>
            <div style={s.modalBody}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 48, fontWeight: 700, color: modal.is_occupied ? '#ffb020' : '#8BC63E', fontFamily: "'Space Mono', monospace" }}>{modal.box_name}</div>
                <div style={{ fontSize: 12, color: '#8789a0', marginTop: 4 }}>Lock: {modal.lock_address} · Size: {modal.box_size}</div>
              </div>
              {modal.is_occupied && (
                <div style={{ background: 'rgba(255,176,32,0.08)', border: '1px solid rgba(255,176,32,0.2)', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13 }}>
                  <div style={{ fontWeight: 700, color: '#ffb020', marginBottom: 8 }}>{modal.is_stale ? (modal.bestwond_order_no ? 'Order only in Bestwond' : 'Occupied — no order found') : 'Parcel Stored'}</div>
                  {modal.order_no && <div style={{ color: '#8789a0', marginBottom: 4 }}>Order: <span style={{ color: '#e4e5ed', fontFamily: "'Space Mono', monospace", fontSize: 12 }}>{modal.order_no}</span></div>}
                  {modal.sender && <div style={{ color: '#8789a0', marginBottom: 4 }}>From: <span style={{ color: '#e4e5ed' }}>{modal.sender}</span></div>}
                  {modal.recipient && <div style={{ color: '#8789a0', marginBottom: 4 }}>To: <span style={{ color: '#e4e5ed' }}>{modal.recipient}</span></div>}
                  {modal.notes && modal.notes !== 'No description' && <div style={{ color: '#8789a0', marginBottom: 4 }}>Contents: <span style={{ color: '#e4e5ed' }}>{modal.notes}</span></div>}
                  {modal.save_time && <div style={{ color: '#8789a0' }}>Stored: <span style={{ color: '#e4e5ed' }}>{modal.save_time}</span></div>}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button style={s.primaryBtn} onClick={() => openBox(modal)} disabled={actionLoading}>
                  <Unlock size={14} /> {actionLoading ? 'Opening...' : 'Open Box'}
                </button>
                {modal.is_door_open ? (
                  <button style={{...s.outlineBtn, borderColor: 'rgba(255,176,32,0.3)', color: '#ffb020'}} onClick={async () => {
                    try { await devicesAPI.markBoxClosed(selectedDevice, modal.id); setModal(null); loadBoxes(); } catch(e) { alert('Error'); }
                  }}>
                    Mark as Closed
                  </button>
                ) : (
                  <button style={s.outlineBtn} onClick={() => setModal(null)}>
                    <Eye size={14} /> Close
                  </button>
                )}
              </div>
              {modal.is_occupied && (
                <div style={{ marginTop: 10 }}>
                  <button style={{...s.outlineBtn, width: '100%', borderColor: 'rgba(255,68,102,0.3)', color: '#ff4466'}} onClick={async () => {
                    if(window.confirm('Force release this box? This will open the door and clear the order.')) {
                      try {
                        const r = await devicesAPI.forceRelease(selectedDevice, modal.id);
                        alert(r.data.message);
                        setModal(null);
                        loadBoxes();
                      } catch(e) { alert(e.response?.data?.detail || 'Error'); }
                    }
                  }}>
                    Force Release Box
                  </button>
                </div>
              )}
              <div style={{ display: 'none' }}>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#e4e5ed' },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: '#8789a0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  select: {
    background: '#161821', border: '1px solid #252733', borderRadius: 8,
    padding: '10px 32px 10px 12px', fontSize: 13, color: '#e4e5ed', outline: 'none',
    WebkitAppearance: 'none', minWidth: 260,
  },
  panel: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, overflow: 'hidden' },
  panelHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #252733' },
  panelTitle: { fontSize: 14, fontWeight: 600, color: '#e4e5ed' },
  legend: { display: 'flex', gap: 16 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#8789a0' },
  legendDot: { width: 8, height: 8, borderRadius: 3 },
  gridWrap: { padding: 20 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 10 },
  cell: {
    aspectRatio: '1', borderRadius: 8, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', border: '1.5px solid',
    cursor: 'pointer', transition: 'transform 0.15s',
  },
  cellName: { fontFamily: "'Space Mono', monospace", fontSize: 16, fontWeight: 700 },
  cellSize: { fontSize: 10, opacity: 0.7, marginTop: 2 },
  cellStatus: { fontSize: 9, marginTop: 4, opacity: 0.6 },
  empty: { padding: 40, textAlign: 'center', color: '#8a8da4', fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: '#8a8da4', background: '#0a0b0e', borderBottom: '1px solid #252733' },
  td: { padding: '12px 16px', fontSize: 13, borderBottom: '1px solid #1a1b25', color: '#e4e5ed' },
  mono: { fontFamily: "'Space Mono', monospace", fontSize: 12 },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  smBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: 'transparent', border: '1px solid #252733', borderRadius: 6, color: '#8789a0', fontSize: 11, fontWeight: 500, cursor: 'pointer' },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 18px', background: '#8BC63E', border: 'none', borderRadius: 8, color: '#07080c', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  outlineBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 18px', background: 'transparent', border: '1px solid #252733', borderRadius: 8, color: '#8789a0', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, width: 400 },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid #252733', color: '#e4e5ed', fontSize: 16, fontWeight: 600 },
  modalBody: { padding: 20 },
};

